<div class="webapp-update-control">
  <div class="row mb-3 align-items-center">
    <div class="col-md-7">
      <p class="text-muted small mb-0">
        Pin POS webapp kiosks to registered releases, compare reported vs target builds, and push soft or forced updates (DEV).
      </p>
      <p *ngIf="isReadOnly" class="text-warning small mb-0 mt-1">
        <nb-icon icon="lock-outline"></nb-icon>
        Read-only — chain managers can view releases and fleet status but cannot change assignments or push updates.
      </p>
    </div>
    <div class="col-md-5">
      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <nb-toggle [(checked)]="autoRefreshEnabled" (checkedChange)="toggleAutoRefresh()" status="primary">
          Auto-refresh
        </nb-toggle>
        <span *ngIf="autoRefreshEnabled" class="text-muted small">Next in {{ countdown }}s</span>
        <button class="btn btn-outline-secondary btn-sm" (click)="refreshAll(true)">
          <nb-icon icon="refresh-outline"></nb-icon>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="endpointUnavailable" class="alert alert-info mb-4">
    <strong>Backend not ready</strong> — {{ errorMessage }}
  </div>

  <div *ngIf="errorMessage && !endpointUnavailable" class="alert alert-danger mb-3">{{ errorMessage }}</div>

  <!-- Actions bar (admin) -->
  <div *ngIf="isAdmin" class="actions-bar mb-4 d-flex flex-wrap align-items-center gap-2">
    <span class="text-muted small me-2">Fleet actions:</span>
    <button class="btn btn-outline-primary btn-sm" (click)="openAssignDialog()">Assign release…</button>
    <button class="btn btn-outline-secondary btn-sm" (click)="openPushDialog(false)">Push update (soft)</button>
    <button class="btn btn-outline-danger btn-sm" (click)="openPushDialog(true)">Force update</button>
    <span class="text-muted small ms-auto">{{ selectedTerminalIds.size }} terminal(s) selected</span>
  </div>

  <!-- Releases -->
  <h5 class="section-heading">Registered releases</h5>
  <div class="d-flex flex-wrap align-items-center gap-3 mb-2">
    <nb-checkbox [(ngModel)]="showRetired" (ngModelChange)="onShowRetiredChange()">Show retired</nb-checkbox>
    <button *ngIf="isAdmin" class="btn btn-sm btn-outline-primary" (click)="openRegisterDialog()">
      Register release manually
    </button>
  </div>

  <nb-card class="mb-4">
    <nb-card-body class="p-0">
      <div *ngIf="isLoading && !releases.length" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
      </div>
      <div class="table-responsive" *ngIf="releases.length">
        <table class="table table-hover table-compact mb-0">
          <thead class="table-light">
            <tr>
              <th>Commit</th>
              <th>Token</th>
              <th>Built at</th>
              <th>Notes</th>
              <th>Badges</th>
              <th>Assigned</th>
              <th *ngIf="isAdmin">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of releases" [class.text-muted]="r.retired">
              <td class="font-monospace"><code>{{ r.commit }}</code></td>
              <td class="font-monospace">
                <span class="token-truncate" [nbTooltip]="r.token">{{ truncateToken(r.token) }}</span>
              </td>
              <td class="small text-nowrap">{{ formatTime(r.builtAt || r.createdAt) }}</td>
              <td class="small">{{ r.releaseNotes || '—' }}</td>
              <td>
                <span *ngIf="r.isLatest" class="badge bg-success me-1">Latest</span>
                <span *ngIf="r.retired" class="badge bg-secondary">Retired</span>
              </td>
              <td class="small">
                {{ r.assignedStoreCount ?? 0 }} stores /
                {{ r.assignedTerminalCount ?? 0 }} terminals
              </td>
              <td *ngIf="isAdmin" class="text-nowrap">
                <button class="btn btn-link btn-sm p-0 me-2" (click)="openEditNotes(r)">Notes</button>
                <button *ngIf="!r.isLatest && !r.retired" class="btn btn-link btn-sm p-0 me-2" (click)="confirmMarkLatest(r)">Mark latest</button>
                <button *ngIf="!r.retired" class="btn btn-link btn-sm p-0 text-danger" (click)="confirmRetire(r, true)">Retire</button>
                <button *ngIf="r.retired" class="btn btn-link btn-sm p-0" (click)="confirmRetire(r, false)">Un-retire</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="!isLoading && !releases.length && !errorMessage" class="p-4 text-center text-muted">
        No releases registered yet.
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Store defaults -->
  <h5 class="section-heading">Store defaults</h5>
  <nb-card class="mb-4">
    <nb-card-body class="p-0">
      <div class="table-responsive" *ngIf="stores.length">
        <table class="table table-compact mb-0">
          <thead class="table-light">
            <tr>
              <th>Store</th>
              <th>Default release</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let s of stores">
              <td>{{ s.storeName }}</td>
              <td>
                <select
                  class="form-select form-select-sm pin-select"
                  [disabled]="isReadOnly || !isAdmin"
                  [ngModel]="s.defaultWebappReleaseId || INHERIT_VALUE"
                  (ngModelChange)="onStoreDefaultChange(s, $event)">
                  <option [value]="INHERIT_VALUE">Inherit latest</option>
                  <option *ngFor="let r of activeReleases()" [value]="r._id">
                    {{ r.commit }} — {{ truncateToken(r.token) }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="!stores.length && !isLoading" class="p-3 text-muted small">No stores in scope.</div>
    </nb-card-body>
  </nb-card>

  <!-- Terminal matrix -->
  <h5 class="section-heading">Terminal matrix</h5>
  <div class="row g-2 mb-3">
    <div class="col-md-4">
      <label class="form-label small mb-1">Store filter</label>
      <select class="form-select form-select-sm" [(ngModel)]="filterStoreId" (ngModelChange)="onFilterStoreChange()">
        <option value="">All stores</option>
        <option *ngFor="let s of stores" [value]="s.storeId">{{ s.storeName }}</option>
      </select>
    </div>
    <div class="col-md-4">
      <label class="form-label small mb-1">Search terminal</label>
      <input class="form-control form-control-sm" [(ngModel)]="terminalSearch" (ngModelChange)="onTerminalSearchChange()" placeholder="Name or ID" />
    </div>
    <div class="col-md-4 d-flex align-items-end" *ngIf="statusData?.latestRelease">
      <span class="text-muted small">
        Fleet latest: <code>{{ statusData?.latestRelease?.commit }}</code>
      </span>
    </div>
  </div>

  <nb-card>
    <nb-card-body class="p-0">
      <div class="table-responsive" *ngIf="filteredTerminals.length">
        <table class="table table-hover table-compact mb-0">
          <thead class="table-light">
            <tr>
              <th *ngIf="isAdmin" style="width: 36px;">
                <input type="checkbox" [checked]="allFilteredSelected()" (change)="toggleSelectAllFiltered($any($event.target).checked)" />
              </th>
              <th>Store</th>
              <th>Terminal</th>
              <th>Online</th>
              <th>Reported</th>
              <th>Target</th>
              <th>Status</th>
              <th>Terminal pin</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let t of filteredTerminals">
              <td *ngIf="isAdmin">
                <input type="checkbox" [checked]="selectedTerminalIds.has(t.terminalId)" (change)="toggleTerminalSelection(t.terminalId, $any($event.target).checked)" />
              </td>
              <td class="small">{{ t.storeName || t.storeId }}</td>
              <td>
                <strong>{{ t.terminalName || t.terminalId }}</strong>
              </td>
              <td class="small text-nowrap">
                <span class="online-dot" [class.online]="t.online" [class.offline]="!t.online"></span>
                {{ t.online ? 'Online' : 'Offline' }}
                <div class="text-muted">{{ formatTime(t.lastSeenAt) }}</div>
              </td>
              <td class="font-monospace small">
                <code *ngIf="t.reportedCommit">{{ t.reportedCommit }}</code>
                <span *ngIf="!t.reportedCommit">—</span>
                <div *ngIf="t.reportedSource" class="text-muted">{{ t.reportedSource }}</div>
              </td>
              <td class="small">
                <code *ngIf="t.target?.commit">{{ t.target.commit }}</code>
                <span *ngIf="!t.target?.commit">—</span>
                <span class="badge source-chip ms-1">{{ targetSourceLabel(t.target?.source) }}</span>
              </td>
              <td>
                <span *ngIf="terminalStatus(t) === 'ok'" class="badge status-chip chip-up-to-date">Up to date</span>
                <span *ngIf="terminalStatus(t) === 'update'" class="badge status-chip chip-update-required">Update required</span>
                <span *ngIf="terminalStatus(t) === 'force'" class="badge status-chip chip-force-pending">Force pending</span>
                <span *ngIf="terminalStatus(t) === 'unknown'" class="badge status-chip chip-unknown">Unknown</span>
              </td>
              <td>
                <select
                  class="form-select form-select-sm pin-select"
                  [disabled]="isReadOnly || !isAdmin"
                  [ngModel]="t.terminalReleaseId || INHERIT_VALUE"
                  (ngModelChange)="onTerminalPinChange(t, $event)">
                  <option [value]="INHERIT_VALUE">Inherit</option>
                  <option *ngFor="let r of activeReleases()" [value]="r._id">
                    {{ r.commit }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="!isLoading && !filteredTerminals.length" class="p-4 text-center text-muted">
        No terminals in scope.
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Register release modal -->
<div *ngIf="showRegisterDialog" class="modal-overlay">
  <nb-card>
    <nb-card-header class="d-flex justify-content-between align-items-center">
      <span>Register release manually</span>
      <button class="btn btn-ghost" (click)="closeRegisterDialog()"><nb-icon icon="close-outline"></nb-icon></button>
    </nb-card-header>
    <nb-card-body>
      <div class="mb-3">
        <label class="form-label">Commit</label>
        <input class="form-control font-monospace" [(ngModel)]="registerForm.commit" />
      </div>
      <div class="mb-3">
        <label class="form-label">Token</label>
        <input class="form-control font-monospace" [(ngModel)]="registerForm.token" />
      </div>
      <div class="mb-3">
        <label class="form-label">Release notes</label>
        <textarea class="form-control" rows="3" [(ngModel)]="registerForm.releaseNotes"></textarea>
      </div>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" (click)="closeRegisterDialog()">Cancel</button>
        <button class="btn btn-primary" (click)="submitRegisterRelease()">Register</button>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Edit notes modal -->
<div *ngIf="editingRelease" class="modal-overlay">
  <nb-card>
    <nb-card-header class="d-flex justify-content-between align-items-center">
      <span>Edit notes — {{ editingRelease.commit }}</span>
      <button class="btn btn-ghost" (click)="closeEditNotes()"><nb-icon icon="close-outline"></nb-icon></button>
    </nb-card-header>
    <nb-card-body>
      <textarea class="form-control mb-3" rows="4" [(ngModel)]="editNotesText"></textarea>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" (click)="closeEditNotes()">Cancel</button>
        <button class="btn btn-primary" (click)="saveReleaseNotes()">Save</button>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Assign dialog -->
<div *ngIf="showAssignDialog" class="modal-overlay">
  <nb-card>
    <nb-card-header class="d-flex justify-content-between align-items-center">
      <span>Assign release</span>
      <button class="btn btn-ghost" (click)="closeAssignDialog()"><nb-icon icon="close-outline"></nb-icon></button>
    </nb-card-header>
    <nb-card-body>
      <div class="mb-3">
        <label class="form-label">Release</label>
        <select class="form-select" [(ngModel)]="assignDialogReleaseId">
          <option [ngValue]="INHERIT_VALUE">Inherit (clear pin / latest for stores)</option>
          <option *ngFor="let r of activeReleases()" [ngValue]="r._id">{{ r.commit }} — {{ r.token }}</option>
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label">Scope</label>
        <select class="form-select" [(ngModel)]="assignDialogScope">
          <option value="selected-terminals">Selected terminals</option>
          <option value="filtered-stores">Current store filter</option>
          <option value="all-stores">All stores</option>
        </select>
      </div>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" (click)="closeAssignDialog()">Cancel</button>
        <button class="btn btn-primary" (click)="submitAssignDialog()">Continue</button>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Push dialog -->
<div *ngIf="showPushDialog" class="modal-overlay">
  <nb-card>
    <nb-card-header class="d-flex justify-content-between align-items-center">
      <span>{{ pushForce ? 'Force update' : 'Push soft update' }}</span>
      <button class="btn btn-ghost" (click)="closePushDialog()"><nb-icon icon="close-outline"></nb-icon></button>
    </nb-card-header>
    <nb-card-body>
      <div class="mb-3">
        <label class="form-label">Scope</label>
        <select class="form-select" [(ngModel)]="pushDialogScope">
          <option value="selected-terminals">Selected terminals</option>
          <option value="filtered-stores">Current store filter</option>
          <option value="all-stores">All stores</option>
        </select>
      </div>
      <div *ngIf="pushForce" class="mb-3">
        <label class="form-label">Message (optional, shown on kiosk)</label>
        <input class="form-control" [(ngModel)]="pushMessage" maxlength="500" />
      </div>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" (click)="closePushDialog()">Cancel</button>
        <button class="btn" [class.btn-danger]="pushForce" [class.btn-primary]="!pushForce" (click)="submitPushDialog()">
          {{ pushForce ? 'Force' : 'Push' }}
        </button>
      </div>
    </nb-card-body>
  </nb-card>
</div>
